import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Badge,
  Button,
  confirm,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ExternalLinkIcon,
  PlusIcon,
  Trash2Icon,
  WalletIcon,
} from "lucide-react";
import { useState } from "react";
import type { PayoutListItemDTO } from "../../domain/contracts";
import { formatDocumentMoney } from "@/shared/lib/performance-document-currency";
import { performanceApi } from "../api/client";
import {
  useDeletePayout,
  useEventPayoutsQuery,
  useEventStaffQuery,
} from "../api/queries";
import { CreateStaffPayoutDialog } from "./CreateStaffPayoutDialog";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";
import { PayoutsSectionSkeleton } from "./EventDetailSectionSkeletons";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return formatDocumentMoney(value, "LAK");
}

type EventStaffPayoutRowProps = {
  payout: PayoutListItemDTO;
  eventId: string;
  canDelete: boolean;
  onOpen: () => void;
};

function EventStaffPayoutRow({
  payout,
  eventId,
  canDelete,
  onOpen,
}: EventStaffPayoutRowProps) {
  const deletePayout = useDeletePayout(eventId);
  const isCancelled = payout.status === "cancelled";
  const canPrint =
    payout.status === "approved" || payout.status === "paid";
  const recipientCount = payout.recipientCount ?? 0;

  return (
    <div
      className={
        isCancelled
          ? "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3"
          : "flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-4 py-3"
      }
    >
      <button
        type="button"
        className="min-w-0 text-left hover:underline disabled:pointer-events-none disabled:no-underline"
        disabled={isCancelled}
        onClick={onOpen}
      >
        <p className="font-semibold text-sm">{payout.payoutNo}</p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          {recipientCount} Staff · {formatMoney(payout.totalAmount)}
        </p>
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_VARIANTS[payout.status] ?? "secondary"}>
          {STATUS_LABELS[payout.status] ?? payout.status}
        </Badge>
        {!isCancelled ? (
          <Button size="sm" variant="outline" className="h-8" onClick={onOpen}>
            <ExternalLinkIcon className="size-4" /> ເປີດ
          </Button>
        ) : null}
        {canPrint ? (
          <PerformanceBrandPrintMenu
            className="h-8 gap-1 px-2"
            buildPrintUrl={(lang) =>
              performanceApi.payouts.printUrl(payout.id, lang)
            }
          />
        ) : null}
        {canDelete && isCancelled ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-destructive hover:bg-destructive/10"
            disabled={deletePayout.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "ລຶບໃບເບີກຈ່າຍ",
                description:
                  "ໃບເບີກຈ່າຍທີ່ຍົກເລີກແລ້ວຈະຖືກລຶບຖາວອນ — ກູ້ຄືນບໍ່ໄດ້",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) deletePayout.mutate(payout.id);
            }}
          >
            <Trash2Icon className="size-4" /> ລຶບ
          </Button>
        ) : null}
      </div>
    </div>
  );
}

type EventStaffPayoutSectionProps = {
  eventId: string;
  payoutReady?: boolean;
  onGoToStaff?: () => void;
};

export function EventStaffPayoutSection({
  eventId,
  payoutReady = true,
  onGoToStaff,
}: EventStaffPayoutSectionProps) {
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  const canCreate = useActionPermission(["performance-payouts:create"]);
  const canDelete = useActionPermission(["performance-payouts:delete"]);
  const payoutsQuery = useEventPayoutsQuery(eventId);
  const staffQuery = useEventStaffQuery(eventId, { activeOnly: true });
  const [createOpen, setCreateOpen] = useState(false);

  const staffPayouts = (payoutsQuery.data ?? []).filter(
    (item) => item.isStaffPayout,
  );
  const activeStaffPayout = staffPayouts.find(
    (item) => item.isBatch && item.status !== "cancelled",
  );
  const cancelledStaffPayout = staffPayouts.find(
    (item) => item.isBatch && item.status === "cancelled",
  );
  const activeStaffCount = staffQuery.data?.length ?? 0;

  const openPayout = (payoutId: string) =>
    nav({
      to: "/app/performance/payouts/$id",
      params: { id: payoutId },
    });

  if (payoutsQuery.isLoading) {
    return <PayoutsSectionSkeleton />;
  }

  return (
    <>
      <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <WalletIcon className="size-4 text-primary" />
            <span className="font-semibold text-sm uppercase tracking-widest">
              ໃບເບີກຈ່າຍ Staff
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => nav({ to: "/app/performance/payouts" })}
          >
            <ExternalLinkIcon className="size-4" /> ເບິ່ງທັງໝົດ
          </Button>
        </div>

        <div className="flex-1 space-y-3 p-5">
          {activeStaffCount === 0 ? (
            <Empty className="py-8">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <WalletIcon className="size-12 text-muted-foreground" />
                </EmptyMedia>
                <EmptyTitle>ຍັງບໍ່ມີ Staff</EmptyTitle>
                <EmptyDescription>
                  ເພີ່ມທີມ Staff ກ່ອນຈຶ່ງສ້າງໃບເບີກຈ່າຍໄດ້
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onGoToStaff?.()}
                >
                  ໄປເພີ່ມ Staff
                </Button>
              </EmptyContent>
            </Empty>
          ) : activeStaffPayout ? (
            <EventStaffPayoutRow
              payout={activeStaffPayout}
              eventId={eventId}
              canDelete={canDelete}
              onOpen={() => openPayout(activeStaffPayout.id)}
            />
          ) : (
            <>
              {cancelledStaffPayout ? (
                <EventStaffPayoutRow
                  payout={cancelledStaffPayout}
                  eventId={eventId}
                  canDelete={canDelete}
                  onOpen={() => openPayout(cancelledStaffPayout.id)}
                />
              ) : null}
              {canCreate ? (
                payoutReady ? (
                  <Button
                    className="w-full sm:w-auto"
                    onClick={() => setCreateOpen(true)}
                  >
                    <PlusIcon className="size-4" /> ສ້າງໃບເບີກຈ່າຍ Staff
                  </Button>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    ຢືນຢັນງານ ຫຼື ອອກໃບສະເໜີລາຄາ/ໃບເກັບເງິນກ່ອນຈຶ່ງສ້າງໃບເບີກຈ່າຍ Staff ໄດ້
                  </p>
                )
              ) : null}
            </>
          )}
        </div>
      </div>

      <CreateStaffPayoutDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        eventId={eventId}
        staff={staffQuery.data ?? []}
      />
    </>
  );
}
